import { forwardRef, useEffect, useRef } from 'react'

import styled from 'styled-components'

import {
  Avatar,
  PersonalUserMenu,
  SText,
  STextEmail,
  TextGroup,
  TitleBox,
  UserMenu,
} from '../styled'
import { User } from '../types'
import { ANNOTATION_OUTSIDE_CLICK_PREVENT_CLASS } from 'features/files-preview/ui/utils/annotationUtils'

export interface MentionSuggestionsProps {
  users: User[]
  activeSuggestionIndex: number
  onSelectUser: (user: User) => void
}

export const MentionSuggestions = forwardRef<
  HTMLDivElement,
  MentionSuggestionsProps
>((props: MentionSuggestionsProps, ref) => {
  const { users, activeSuggestionIndex, onSelectUser } = props
  const itemRefs = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    const currentItem = itemRefs.current[activeSuggestionIndex]
    if (currentItem) {
      currentItem.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
  }, [activeSuggestionIndex])

  if (!users.length) {
    return null
  }

  return (
    <UserMenu ref={ref}>
      {users.map((user, index) => (
        <PersonalUserMenu
          key={user.id}
          ref={el => (itemRefs.current[index] = el)}
          $isSelected={activeSuggestionIndex === index}
          onMouseDown={e => {
            e.preventDefault() // Prevent editor from losing focus
            onSelectUser(user)
          }}
          className={ANNOTATION_OUTSIDE_CLICK_PREVENT_CLASS}
        >
          <SAvatar
            src={user.avatarUrl || undefined}
          >
            {user.name?.[0] || '?'}
          </SAvatar>

          <TextGroup>
            <TitleBox>
              <SText>
                {user.name}
              </SText>
            </TitleBox>

            <STextEmail>
              {user.phone_or_email || ''}
            </STextEmail>
          </TextGroup>
        </PersonalUserMenu>
      ))}
    </UserMenu>
  )
})

MentionSuggestions.displayName = 'MentionSuggestions'

const SAvatar = styled(Avatar)`
  border-radius: 50%;
`
